import { MutableRefObject } from "react";
import * as THREE from "three";
import { ExtrudeGeometry, Vector2 } from "three";

//OG
// const getPlankAmount = (
// 	arc: THREE.Mesh | null,
// 	doorWidth: number,
// 	frameWidth: number,
// 	plankWidth: number,
// 	minGap: number,
// 	depth: number
// ) => {
// 	if (!arc || !arc.geometry) return { plankWidth, planks: [] };
// 	const innerDoorWidth = doorWidth - frameWidth * 2;
// 	const extrudeGeometry = arc.geometry as THREE.ExtrudeGeometry;

// 	// Ensure we have a valid number of planks that fit within the inner door width
// 	let plankAmount = Math.floor(innerDoorWidth / (plankWidth + minGap));

// 	// Calculate the total width occupied by the planks with the minimum gap
// 	let totalPlankWidth = plankAmount * plankWidth;

// 	// Calculate remaining space for gaps
// 	let remainingSpace = innerDoorWidth - totalPlankWidth;

// 	// Distribute the gap evenly
// 	let gap = remainingSpace / (plankAmount + 1);

// 	// Ensure gap is not smaller than minGap
// 	if (gap < minGap) {
// 		plankAmount = Math.floor(innerDoorWidth / (plankWidth + minGap));
// 		totalPlankWidth = plankAmount * plankWidth;
// 		remainingSpace = innerDoorWidth - totalPlankWidth;
// 		gap = remainingSpace / (plankAmount + 1);
// 	}

// 	const plankHeight = extrudeGeometry.parameters.shapes.curves[0].curves[0].getPoints(plankAmount - 1);

// 	const planks = Array.from({ length: plankAmount }, (_, i) => {
// 		const x = gap + i * (plankWidth + gap);
// 		return {
// 			x: x + plankWidth / 2 + frameWidth,
// 			y: (plankHeight[i]?.y - frameWidth / 3) / 2,
// 			z: depth / 2,
// 			height: plankHeight[i]?.y - frameWidth / 3,
// 		};
// 	});

// 	return {
// 		plankWidth,
// 		planks,
// 	};
// };

const calculatePlankAmount = (innerDoorWidth: number, plankWidth: number, minGap: number) => {
	let plankAmount = Math.floor(innerDoorWidth / (plankWidth + minGap));
	let totalPlankWidth = plankAmount * plankWidth;
	let remainingSpace = innerDoorWidth - totalPlankWidth;
	let gap = remainingSpace / (plankAmount + 1);

	if (gap < minGap) {
		plankAmount = Math.floor(innerDoorWidth / (plankWidth + minGap));
		totalPlankWidth = plankAmount * plankWidth;
		remainingSpace = innerDoorWidth - totalPlankWidth;
		gap = remainingSpace / (plankAmount + 1);
	}

	return { plankAmount, gap };
};

const getPlankHeights = (
	extrudeGeometry: ExtrudeGeometry,
	plankAmount: number,
	plankWidth: number,
	centerHeight: number,
	outerHeight: number
) => {
	const shapes = extrudeGeometry.parameters.shapes;
	const shape = Array.isArray(shapes) ? shapes[0] : shapes;
	const isShapeIncreasing = centerHeight < outerHeight;
	let path = shape.curves[0].curves[0];
	let isCurve = path.type === "QuadraticBezierCurve";

	let points = [];
	let offsetHeight = 0;

	if (isCurve) {
		points = path.getPoints(isShapeIncreasing ? plankAmount : plankAmount + 1);
		offsetHeight = 0.002;
	} else {
		points = path.getPoints(plankAmount + 1);
		offsetHeight = 0.01;
	}

	const createPlanks = () => {
		let heights = points.map((point, index) => {
			// Get previous and next points to calculate slope
			const prevPoint = index > 0 ? points[index - 1] : point;
			const nextPoint = index < points.length - 1 ? points[index + 1] : point;

			let slopeMultiplier = 1;

			// Calculate slope with reduced intensity
			const dx = nextPoint.x - prevPoint.x;
			const dy = nextPoint.y - prevPoint.y;
			const slope = dx !== 0 ? (dy / dx) * slopeMultiplier : 0; // Reduced multiplier to 0.2 for gentler slope

			let offsetMultiplier = isCurve ? 2 : 1;

			// Calculate y-offset based on slope and plankWidth
			const yOffset = (plankWidth / 2) * slope;

			let offset = isShapeIncreasing ? (slope * plankWidth) / offsetMultiplier : slope * plankWidth * offsetMultiplier;
			// Calculate points half plankWidth to the left and right of the center point
			const lowPoint = new Vector2(point.x - plankWidth / 2, point.y - (yOffset - offset));
			const highPoint = new Vector2(point.x + plankWidth / 2, point.y + (yOffset + offset));

			return {
				low: [lowPoint.x, lowPoint.y - offsetHeight],
				high: [highPoint.x, highPoint.y - offsetHeight],
			};
		});

		return {
			points: points,
			heights: heights,
		};
	};

	const planks = createPlanks();

	return {
		centerHeights: planks.points,
		distributedHeight: planks.heights,
	};
};

const generatePlankPositions = (
	plankAmount: number,
	gap: number,
	plankWidth: number,
	frameWidth: number,
	depth: number,
	plankHeights: Vector2[],
	distributedHeight: Vector2[]
) => {
	return Array.from({ length: plankAmount }, (_, i) => {
		const x = gap + i * (plankWidth + gap);
		return {
			x: x + plankWidth / 2 + frameWidth,
			y: (plankHeights[i]?.y - frameWidth / 3) / 2,
			z: 0,
			height: plankHeights[i]?.y - frameWidth / 3,
			distributedHeight: distributedHeight[i],
		};
	});
};

export const getPlankAmount = (
	arc: MutableRefObject<THREE.Mesh | null>,
	doorWidth: number,
	frameWidth: number,
	plankWidth: number,
	minGap: number,
	depth: number,
	centerHeight: number,
	outerHeight: number
) => {
	if (!arc.current || !arc.current.geometry) return { plankWidth, planks: [] };

	if (!(arc.current.geometry instanceof THREE.ExtrudeGeometry)) {
		return { plankWidth, planks: [] };
	}

	const innerDoorWidth = doorWidth - frameWidth * 2;
	const extrudeGeometry = arc.current.geometry as THREE.ExtrudeGeometry;

	const { plankAmount, gap } = calculatePlankAmount(innerDoorWidth, plankWidth, minGap);

	// works if path is quadratic bezier curve or straight line
	const plankHeights = getPlankHeights(extrudeGeometry, plankAmount, plankWidth, centerHeight, outerHeight);

	// plank height code for cubic bezier curve comes here

	const planks = generatePlankPositions(
		plankAmount,
		gap,
		plankWidth,
		frameWidth,
		depth,
		plankHeights.centerHeights,
		plankHeights.distributedHeight
	);

	return {
		plankWidth,
		planks,
	};
};
